/**
 * PATH: packages/ui/src/kanban/components/KanbanCardLabelBars.tsx
 * FILE_ID: UI-KANBAN-CARD-LABELBARS-001
 * PURPOSE: Compact-density label indicator — slim coloured bars that EXPAND on
 *   card hover to spell out each label's name (Trello's collapsed-labels
 *   pattern, hover-reveal instead of click-toggle).
 * WHY: FEAT-2026-07-04-KANBAN-LABEL-HOVER-NAME — operator: "Looks nice, now on
 *   hover also clearly show the name". Compact tiles only carried the label
 *   colour (right-edge accent / bar) with no way to learn WHICH label it is
 *   without opening the card; expanded tiles already show full name pills
 *   (KanbanCardTags).
 * FLOW:
 *   card.tags → colour bars (resting) ──group-hover──▶ tinted chips with the
 *   label name text (+ native title tooltip for precision hovers).
 * DEPENDENCIES:
 *   - ../utils readableForeground: luminance-safe text colour on the fill.
 * RELATED:
 *   - ./KanbanCardTags.tsx — the expanded-density pill row (names always on).
 *   - ./KanbanCard.tsx — renders this in compact density only.
 */
import type { Card as KanbanCardModel } from '../types'
import { readableForeground } from '../utils'

export interface KanbanCardLabelBarsProps {
  card: KanbanCardModel
}

export function KanbanCardLabelBars({ card }: KanbanCardLabelBarsProps) {
  const tags = (card.tags ?? []).filter((t) => !t.revoked_at && t.tag)
  if (tags.length === 0) return null
  return (
    <div className="mb-0.5 flex flex-wrap items-center gap-1" data-testid="kanban-card-label-bars">
      {tags.slice(0, 4).map((t) => {
        const color = t.tag.color || '#94a3b8'
        const fg = readableForeground(color)
        return (
          <span
            key={t.id}
            data-testid={`kanban-card-label-bar-${t.tag.id}`}
            title={t.tag.name}
            className={[
              // Resting: a slim colour bar. Card hover (root `group`): grows
              // into a chip and reveals the name. Overflow-hidden + max-w
              // transition keeps the reveal smooth and truncation safe.
              'inline-flex h-1.5 min-w-6 items-center overflow-hidden rounded-full',
              'transition-all duration-150 ease-out',
              'group-hover:h-3.5 group-hover:px-1.5',
            ].join(' ')}
            style={{
              background: color,
              boxShadow: t.tag.accent_color ? `inset 0 0 0 1.5px ${t.tag.accent_color}` : undefined,
            }}
          >
            <span
              className="max-w-0 truncate text-[9.5px] font-semibold leading-none opacity-0 transition-all duration-150 group-hover:max-w-[140px] group-hover:opacity-100"
              style={{ color: fg }}
            >
              {t.tag.name}
            </span>
          </span>
        )
      })}
      {tags.length > 4 && (
        <span
          className="self-center text-[9px] font-medium leading-none text-neutral-400 dark:text-zinc-500"
          title={tags.slice(4).map((t) => t.tag.name).join(', ')}
        >
          +{tags.length - 4}
        </span>
      )}
    </div>
  )
}
